// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Clickable from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getNumberTextField from "@store/Framework/Common/Fields/Toolkit/numberText";
import getTextAlignmentField, { textAlignmentOptions } from "@store/Framework/Common/Fields/Toolkit/textAlignment";
import getVerticalAlignmentField, {
    verticalAlignmentOptions,
} from "@store/Framework/Common/Fields/Toolkit/verticalAlignment";
import FlexWrapper, { FlexWrapperPresentationProps } from "@store/Library/Components/Toolkit/FlexWrapper";
import { widgetWrapperStyles } from "@store/Library/Components/Toolkit/WidgetWrapper";
import Carousel, {
    CarouselPresentationProps,
    CarouselSlide,
    getItemsPerSlideFromFieldValue,
} from "@store/Overrides/Components/Carousel";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { focusUpdateStyles } from "@toolkit/Themes/common/mixins/common";
import React, { createRef, FC, useMemo } from "react";
import { css } from "styled-components";

const imagesGap = pxToRem(30);

const enum fields {
    images = "images",
    imagesHorizontalAlignment = "imagesHorizontalAlignment",
    imagesVerticalAlignment = "imagesVerticalAlignment",
    maximumNumberOfImagesPerRow = "maximumNumberOfImagesPerRow",
    maximumImageHeight = "maximumImageHeight",
    rowsAlignment = "rowsAlignment",
    showCarousel = "showCarousel",
    showDots = "showDots",
    title = "title",
    titleAlignment = "titleAlignment",
}

const imagesHorizontalAlignmentField = getTextAlignmentField(fields.imagesHorizontalAlignment);
const imagesPerRowField = getNumberTextField(fields.maximumNumberOfImagesPerRow, { integer: true, min: 1 });
const imagesVerticalAlignmentField = getVerticalAlignmentField(fields.imagesVerticalAlignment);
const rowsAlignmentField = getTextAlignmentField(fields.rowsAlignment);
const titleAlignmentField = getTextAlignmentField(fields.titleAlignment);

const enum imageFields {
    altText = "altText",
    id = "id",
    link = "link",
    path = "path",
}

interface ImageProps {
    image: {
        altText: string;
        id: string;
        link: LinkFieldValue;
        path: string;
    };
    imgProps?: LazyImageProps["imgProps"];
}

interface ImagesFields {
    fields: {
        [imageFields.altText]: string;
        [imageFields.id]: string;
        [imageFields.link]: LinkFieldValue;
        [imageFields.path]: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.images]: ImagesFields[];
        [fields.imagesHorizontalAlignment]: string;
        [fields.imagesVerticalAlignment]: string;
        [fields.maximumNumberOfImagesPerRow]: string;
        [fields.maximumImageHeight]: number;
        [fields.rowsAlignment]: string;
        [fields.showCarousel]: boolean;
        [fields.showDots]: boolean;
        [fields.titleAlignment]: string;
    };
}

export interface ImageGalleryStyles {
    titleText?: TypographyProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    flexWrapper?: FlexWrapperPresentationProps;
    carousel?: CarouselPresentationProps;
    link?: LinkProps;
    image?: LazyImageProps;
}

const imageGalleryStyles: ImageGalleryStyles = {
    titleText: { variant: "h2" },
    container: widgetWrapperStyles,
    carousel: {
        arrowsGap: pxToRem(20),
        extendedStyles: {
            carouselSlide: {
                css: css`
                    display: flex;
                    flex-direction: column;
                `,
            },
        },
        itemsGap: imagesGap,
    },
    flexWrapper: {
        gap: imagesGap,
        extendedStyles: {
            content: { forwardAs: "ul" },
            item: {
                forwardAs: "li",
                css: css`
                    z-index: 0;
                `,
            },
        },
    },
    link: {
        mergeCss: true,
        css: css`
            ${focusUpdateStyles(true)}
        `,
    },
    image: {
        mergeCss: true,
        css: css`
            z-index: -1;
        `,
    },
};

const styles = imageGalleryStyles;

const Image: FC<ImageProps> = ({ image, imgProps }) => {
    const { url } = useGetLink(image.link);
    const img = <LazyImage {...styles.image} imgProps={imgProps} src={image.path} altText={image.altText} />;

    return url ? (
        <Clickable {...styles.link} href={url}>
            {img}
        </Clickable>
    ) : (
        img
    );
};

const ImageGallery: FC<OwnProps> = ({
    fields: {
        images,
        imagesHorizontalAlignment: baseImagesHorizontalAlignment,
        imagesVerticalAlignment: baseImagesVerticalAlignment,
        maximumImageHeight,
        maximumNumberOfImagesPerRow,
        rowsAlignment: baseRowsAlignment,
        showCarousel,
        showDots,
        title,
        titleAlignment: baseTitleAlignment,
    },
    id,
}) => {
    const afterGallery = createRef<HTMLSpanElement>();
    const imagesHorizontalAlignment = imagesHorizontalAlignmentField.useParseToFlex(baseImagesHorizontalAlignment);
    const imagesPerRow = getItemsPerSlideFromFieldValue(maximumNumberOfImagesPerRow, imagesPerRowField.parse);
    const imagesVerticalAlignment = imagesVerticalAlignmentField.useParseToFlex(baseImagesVerticalAlignment);
    const { isEditing } = useShellContext();
    const rowsAlignment = rowsAlignmentField.useParseToFlex(baseRowsAlignment);
    const titleAlignment = titleAlignmentField.parse(baseTitleAlignment);

    const imgProps = useMemo(
        () => ({
            style: {
                width: "auto",
                maxWidth: "100%",
                maxHeight: maximumImageHeight ? pxToRem(maximumImageHeight) : "none",
            },
        }),
        [maximumImageHeight],
    );

    const carouselStyles = useMemo(() => {
        return mergeProps<ImageGalleryStyles["carousel"]>(styles.carousel, {
            extendedStyles: {
                carouselSlide: {
                    css: css`
                        ${cssProp("align-items", imagesHorizontalAlignment)}
                    `,
                },
            },
        });
    }, [imagesHorizontalAlignment, styles.carousel]);

    const flexWrapperStyles = useMemo(() => {
        return mergeProps<ImageGalleryStyles["flexWrapper"]>(styles.flexWrapper, {
            extendedStyles: {
                content: {
                    css: css`
                        ${cssProp("align-items", imagesVerticalAlignment)}
                        ${cssProp("justify-content", rowsAlignment)}
                    `,
                },
                item: {
                    css: css`
                        ${cssProp("justify-content", imagesHorizontalAlignment)}
                    `,
                },
            },
        });
    }, [imagesHorizontalAlignment, imagesVerticalAlignment, rowsAlignment, styles.carousel]);

    const titleStyles = useMemo(() => {
        return mergeProps<ImageGalleryStyles["titleText"]>(styles.titleText, {
            css: css`
                ${cssProp("text-align", titleAlignment)}
            `,
        });
    }, [styles.titleText, titleAlignment]);

    if (!images.length && !isEditing) {
        return null;
    }

    const slides: CarouselSlide[] = images.map(({ fields: image }, index) => ({
        id: image.id,
        renderComponent: () => {
            return <Image key={image.id || index} image={image} imgProps={imgProps} />;
        },
    }));
    const titleId = `image-gallery-title-${id}`;

    return (
        <>
            {Boolean(title) && (
                <Typography {...titleStyles} id={titleId}>
                    {title}
                </Typography>
            )}
            <StyledWrapper {...styles.container}>
                <SkipNav
                    destination={afterGallery}
                    extendedStyles={styles.skipToContent}
                    text={translate("List of {0} items, skip list?", `${images.length}`)}
                />
                {showCarousel ? (
                    <Carousel
                        {...carouselStyles}
                        id={`image-gallery-${id}`}
                        itemsAlign={imagesVerticalAlignment}
                        itemsJustify={rowsAlignment}
                        itemsPerSlide={imagesPerRow}
                        showDots={showDots}
                        slides={slides}
                        titleId={title ? titleId : undefined}
                        useSkipNav={false}
                    />
                ) : (
                    <FlexWrapper {...flexWrapperStyles} itemsPerRow={imagesPerRow}>
                        {slides.map(slide => slide.renderComponent())}
                    </FlexWrapper>
                )}
                <span ref={afterGallery} tabIndex={-1} />
            </StyledWrapper>
        </>
    );
};

const carouselTab = { displayName: "Carousel", sortOrder: 1 };
const advancedTab = { displayName: "Advanced", sortOrder: 2 };

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: ImageGallery,
    definition: {
        group: "Basic",
        icon: "image",
        displayName: "Image Gallery",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "",
            },
            {
                ...titleAlignmentField.definition,
                displayName: "Title Alignment",
                fieldType: "Contextual",
                defaultValue: textAlignmentOptions.center.id,
                isVisible: x => Boolean(x.fields.title),
            },
            {
                ...imagesPerRowField.definition,
                displayName: "Images per row",
                fieldType: "Contextual",
                defaultValue: "6",
            },
            {
                name: fields.maximumImageHeight,
                displayName: "Maximum image height",
                editorTemplate: "IntegerField",
                defaultValue: 100,
                fieldType: "Contextual",
                tooltip: `Forces a maximum height on the images to scale them down`,
                tooltipPosition: "left",
            },
            {
                name: fields.images,
                displayName: "Images",
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: item => item.fields.altText,
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: imageFields.id,
                        editorTemplate: "UniqueIdField",
                    },
                    {
                        name: imageFields.path,
                        defaultValue: "",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                    },
                    {
                        name: imageFields.altText,
                        defaultValue: "",
                        displayName: "Alt Text",
                        editorTemplate: "TextField",
                    },
                    {
                        name: imageFields.link,
                        displayName: "Link",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                    },
                ],
            },
            {
                name: fields.showCarousel,
                displayName: "Show Carousel",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
                tab: carouselTab,
            },
            {
                name: fields.showDots,
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
                isVisible: item => !!item.fields.showCarousel,
                tab: carouselTab,
            },
            {
                ...rowsAlignmentField.definition,
                displayName: "Rows Alignment",
                fieldType: "Contextual",
                defaultValue: textAlignmentOptions.left.id,
                tooltip: `Aligns the rows horizontally, only visible when there is not enough images to fill a row`,
                tooltipPosition: "left",
                tab: advancedTab,
            },
            {
                ...imagesHorizontalAlignmentField.definition,
                displayName: "Images Horizontal Alignment",
                fieldType: "Contextual",
                defaultValue: textAlignmentOptions.center.id,
                tooltip: `Aligns the images horizontally, only visible when an image is not large enough to fill its column`,
                tooltipPosition: "left",
                tab: advancedTab,
            },
            {
                ...imagesVerticalAlignmentField.definition,
                displayName: "Images Vertical Alignment",
                fieldType: "Contextual",
                defaultValue: verticalAlignmentOptions.middle.id,
                tooltip: `Aligns the images vertically, only visible when images have different heights in the same row`,
                tooltipPosition: "left",
                tab: advancedTab,
            },
        ],
    },
};

export default widgetModule;
